/**
 * React Query hooks for an agent's built-in tools (the `/api/tools/` catalog +
 * `/api/agents/{id}/tools/` per-agent set). Same token mechanism as the other
 * agent hooks ({@link useSquadAppToken}); queries stay disabled until the
 * app-token resolves.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  listAgentTools,
  listToolCatalog,
  setAgentTools,
  type AgentToolWrite
} from 'src/services/agentTools'

import { useSquadAppToken } from '../../squad'

import { agentQueryKeys } from './useWorkspaceAgents'

export const toolQueryKeys = {
  catalog: (workspaceId: number) =>
    ['squad', 'tools', workspaceId, 'catalog'] as const,
  agentTools: (workspaceId: number, agentId: string) =>
    ['squad', 'tools', workspaceId, 'agent', agentId] as const
}

export const useToolCatalog = (workspaceId: number) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: toolQueryKeys.catalog(workspaceId),
    queryFn: () => listToolCatalog(appToken!),
    enabled: Boolean(appToken)
  })
}

export const useAgentTools = (
  workspaceId: number,
  agentId: string | undefined
) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: toolQueryKeys.agentTools(workspaceId, agentId ?? ''),
    queryFn: () => listAgentTools(appToken!, agentId!),
    enabled: Boolean(appToken && agentId)
  })
}

/** Replace the agent's full tool set (enable/disable + config). */
export const useSetAgentTools = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tools: AgentToolWrite[]) =>
      setAgentTools(appToken!, agentId, tools),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: toolQueryKeys.agentTools(workspaceId, agentId)
      })
      void queryClient.invalidateQueries({
        queryKey: agentQueryKeys.detail(workspaceId, agentId)
      })
    }
  })
}
